import * as React from 'react';
import type { CurrencyCode } from '@insio/shared-types';
import { cn, getIssueExpected } from '../../../utils';
import { ApplyExpectedButton, ErrorEntry } from '../../../components/errors';
import { DecimalInput, Input } from '../../../components/inputs';
import type { ValidationIssue } from '../../../types';
import { useDebouncedInputValue } from '../../../hooks';

const labelClass = cn(
  'block text-xs font-medium text-foreground-muted whitespace-nowrap overflow-hidden text-ellipsis text-left'
);

const INPUT_BASE = 'w-full min-w-16 text-sm';
const NUMERIC_INPUT = cn(INPUT_BASE, '@3xl/lines:text-right');

export interface InvoiceLineFieldProps {
  label: string;
  htmlFor: string;
  isError?: boolean;
  issue?: ValidationIssue;
  align?: 'left' | 'right';
  /** Medium-layout `col-span` class (one of `SPAN_*` from `InvoiceLine`). */
  className?: string;
  /** Optional slot rendered below the issue line; used for the
   * `ExpectedAmountHint` on total inputs. */
  hint?: React.ReactNode;
  /** When set and the worst `issue` carries `meta.expected`, an apply arrow is
   * shown next to the issue message to fill the field with that value. */
  onApplyExpected?: () => void;
  /** aria-label / title for the apply button. */
  applyExpectedLabel?: string;
  children: React.ReactNode;
  visible?: boolean;
}

/**
 * Single labelled cell in an `InvoiceLine` row: label on top, an input slot,
 * an optional `ErrorEntry` for the worst validation issue on the field, and
 * an optional `hint` slot used for `ExpectedAmountHint`. Lives next to
 * `InvoiceLine.tsx` because the responsive layout assumes the `@3xl/lines`
 * container query is set by the surrounding `InvoiceLinesForm`.
 */
export const InvoiceLineField: React.FC<InvoiceLineFieldProps> = ({
  label,
  htmlFor,
  isError,
  issue,
  align = 'left',
  className: spanClass,
  hint,
  onApplyExpected,
  applyExpectedLabel,
  children,
  visible = true
}) => {
  if (!visible) return null;
  const canApplyExpected = !!onApplyExpected && !!issue && getIssueExpected(issue) != null;
  return (
    <div className={cn('flex flex-col gap-2xs', spanClass)}>
      <label htmlFor={htmlFor} className={labelClass}>
        {label}
      </label>
      {children}
      {issue && (
        <p
          className={cn(
            'text-2xs leading-tight',
            align === 'right' ? '@3xl/lines:text-right' : '@3xl/lines:text-left',
            isError ? 'text-error-text' : 'text-warn-text'
          )}
        >
          <ErrorEntry issue={issue} />
          {canApplyExpected && (
            <ApplyExpectedButton onApply={onApplyExpected} label={applyExpectedLabel ?? ''} />
          )}
        </p>
      )}
      {hint}
    </div>
  );
};

InvoiceLineField.displayName = 'InvoiceLineField';

type LineFieldShellProps = Omit<InvoiceLineFieldProps, 'children' | 'htmlFor'>;

export interface TextLineFieldProps extends LineFieldShellProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Extra classes layered on top of the shared text-input base. */
  inputClassName?: string;
  visible?: boolean;
  className?: string;
}

/**
 * `InvoiceLineField` + text `Input`. Owns the shared text-input base class
 * and the event-to-string unwrapping so callers pass plain `value`/`onChange`.
 * Keystrokes render immediately; `onChange` fires debounced (200 ms idle) and
 * on blur, so the parent form isn't re-rendered per keypress.
 */
export const TextLineField: React.FC<TextLineFieldProps> = ({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  inputClassName,
  className,
  visible = true,
  ...fieldProps
}) => {
  const { inputValue, handleChange, flush } = useDebouncedInputValue(value, onChange);
  if (!visible) return null;
  return (
    <InvoiceLineField {...fieldProps} className={className} htmlFor={id}>
      <Input
        id={id}
        type='text'
        value={inputValue}
        onChange={e => handleChange(e.target.value)}
        onBlur={flush}
        disabled={disabled}
        height='sm'
        placeholder={placeholder}
        className={cn(INPUT_BASE, inputClassName)}
      />
    </InvoiceLineField>
  );
};

TextLineField.displayName = 'TextLineField';

export interface NumericLineFieldProps extends Omit<LineFieldShellProps, 'align'> {
  id: string;
  value: string;
  onChange: (raw: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  /** Extra classes layered on top of the shared numeric-input base. */
  inputClassName?: string;
  /**
   * When provided, renders `DecimalInput` so the cell gets currency-aware
   * `step`, unfocused padding, blur-time rounding, and excess-precision
   * debounced rounding. Omit for non-currency numerics (quantity, percent)
   * where a plain `<Input type="text">` lets users type comma as a decimal
   * separator.
   */
  currencyCode?: CurrencyCode | null;
}

/**
 * `InvoiceLineField` + numeric `Input`. Always right-aligned, selects on focus
 * (so a single tap replaces the prior value), and commits formatting on blur.
 * Keystrokes render immediately; `onChange` fires debounced (200 ms idle) and
 * is flushed on blur before `onBlur`, so the blur-commit sees the latest text.
 */
export const NumericLineField: React.FC<NumericLineFieldProps> = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  inputClassName,
  currencyCode,
  className,
  ...fieldProps
}) => {
  const { inputValue, handleChange, flush } = useDebouncedInputValue(value, onChange);
  const handleBlur = () => {
    flush();
    onBlur();
  };
  return (
    <InvoiceLineField {...fieldProps} className={className} htmlFor={id} align='right'>
      {currencyCode != null ? (
        <DecimalInput
          id={id}
          currencyCode={currencyCode}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={e => e.target.select()}
          disabled={disabled}
          height='sm'
          className={cn(NUMERIC_INPUT, inputClassName)}
        />
      ) : (
        <Input
          id={id}
          type='text'
          value={inputValue}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleBlur}
          onFocus={e => e.target.select()}
          disabled={disabled}
          height='sm'
          className={cn(NUMERIC_INPUT, inputClassName)}
        />
      )}
    </InvoiceLineField>
  );
};

NumericLineField.displayName = 'NumericLineField';
